/**
 * SalesByManagerCard (KAN-783)
 *
 * Dashboard block "Продажи по менеджерам" (e-commerce cabinet): orders sent
 * for review or approved per manager for the period. Pills Неделя / Месяц /
 * Период (custom dates through CrmRangePicker), table Менеджер / Заказов /
 * Сумма, the row "Без менеджера" and the total. Default period: this month.
 * Data: GET /api/v1/dashboard/sales-by-manager/?date_from&date_to.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CrmRangePicker } from '@/components/crm/CrmRangePicker'
import { formatCrmMoney } from '@/lib/crmBoard'
import { todayMoscow, type CrmRange } from '@/lib/crmTable'
import { extractErrorMessage } from '@/lib/extractErrorMessage'
import { SALES_PERIOD_TABS, rangeFor, type SalesPeriodPreset } from '@/lib/salesPeriod'
import { dashboardApi, type SalesByManager } from '@/services/api/dashboard'
import { SALES_BY_MANAGER_QUERY_KEY } from '@/services/api/queryKeys'
import { PillTabs } from './PillTabs'

const DEFAULT_PRESET: SalesPeriodPreset = 'month'
const NO_MANAGER = 'Без менеджера'

const TH = 'py-1.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500'
const TD = 'py-1.5 text-sm'
const NUM = 'text-right tabular-nums'

export function SalesByManagerCard() {
  const today = todayMoscow()
  const [preset, setPreset] = useState<SalesPeriodPreset>(DEFAULT_PRESET)
  const [range, setRange] = useState<CrmRange>(() => rangeFor(DEFAULT_PRESET, today))
  const { data, isLoading, error } = useQuery<SalesByManager>({
    queryKey: [...SALES_BY_MANAGER_QUERY_KEY, range.from, range.to],
    queryFn: () => dashboardApi.getSalesByManager({ date_from: range.from, date_to: range.to }),
    retry: false,
  })

  const onPreset = (next: SalesPeriodPreset) => {
    setPreset(next)
    setRange(rangeFor(next, today, range))
  }

  let body: JSX.Element
  if (isLoading) {
    body = (
      <div data-testid="sales-loading" className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    )
  } else if (error || !data) {
    body = (
      <p data-testid="sales-error" className="py-6 text-center text-sm text-red-600">
        Не удалось загрузить продажи: {extractErrorMessage(error, 'ошибка сервера')}
      </p>
    )
  } else if (data.rows.length === 0) {
    body = (
      <p data-testid="sales-empty" className="py-6 text-center text-sm text-gray-500">
        Заказов за период нет
      </p>
    )
  } else {
    body = (
      <table className="w-full" data-testid="sales-table">
        <thead>
          <tr className="border-b">
            <th className={TH}>Менеджер</th>
            <th className={`${TH} ${NUM}`}>Заказов</th>
            <th className={`${TH} ${NUM}`}>Сумма</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row) => (
            <tr key={row.manager?.id ?? 'none'} className="border-b last:border-0" data-testid="sales-row">
              <td className={`${TD} ${row.manager ? 'text-gray-900' : 'text-gray-500'}`}>
                {row.manager?.name ?? NO_MANAGER}
              </td>
              <td className={`${TD} ${NUM}`}>{row.orders}</td>
              <td className={`${TD} ${NUM}`}>{formatCrmMoney(row.total, data.currency)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t font-semibold" data-testid="sales-total">
            <td className={TD}>Итого</td>
            <td className={`${TD} ${NUM}`}>{data.total.orders}</td>
            <td className={`${TD} ${NUM}`}>{formatCrmMoney(data.total.total, data.currency)}</td>
          </tr>
        </tfoot>
      </table>
    )
  }

  return (
    <Card data-testid="sales-by-manager-card">
      <CardHeader className="space-y-3">
        <CardTitle>Продажи по менеджерам</CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          <PillTabs label="Период" tabs={SALES_PERIOD_TABS} value={preset} onChange={onPreset} testIdPrefix="sales-period" />
          {preset === 'custom' && <CrmRangePicker range={range} onChange={setRange} />}
        </div>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  )
}
